import { Button } from '@heroui/react'
import { ApiError, useGetInvitationInfo } from '@qse/api'
import { AppRoutesList } from '@qse/constants'
import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

export default function CheckInvitation() {
  const { tenantId, invitationCode } = useParams<{ tenantId: string; invitationCode: string }>()

  const { data, isLoading, error } = useGetInvitationInfo(tenantId || '', invitationCode || '')
  const navigate = useNavigate()

  useEffect(() => {
    if (!tenantId || !invitationCode) {
      navigate('/login')

      return
    }

    if (error) {
      // Extract specific error message from API response
      let errorMessage = 'The invitation link is invalid or has expired.'

      if (error instanceof ApiError) {
        const backendError = error.response?.data?.error ?? error.response?.data ?? {}

        errorMessage = backendError.message || error.message || errorMessage
      } else if (error instanceof Error) {
        errorMessage = error.message || errorMessage
      }

      // Show toast with specific error message
      toast.error(errorMessage)

      navigate('/login')

      return
    }

    if (data && !isLoading) {
      if (data.isAccepted) {
        navigate('/login')

        return
      }

      navigate(AppRoutesList.completeRegistration, {
        state: {
          invitationData: data,
          tenantId,
          invitationCode,
        },
      })
    }
  }, [data, error, tenantId, invitationCode, navigate, isLoading])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Checking invitation...</p>
        </div>
      </div>
    )
  }

  if (error && !isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#18181b] px-4">
        <div className="bg-[#232328] dark:bg-[#232328] rounded-xl shadow-lg p-8 max-w-md w-full text-center transition-colors duration-300">
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center justify-center rounded-full bg-red-500/10 dark:bg-red-500/10 h-14 w-14">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" fill="none" r="10" stroke="currentColor" strokeWidth="2" />
                <path
                  d="M12 8v4m0 4h.01"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
            </span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">Invalid Invitation</h2>
          <p className="text-gray-400 mb-8 text-base leading-relaxed">
            The invitation link is invalid or has expired.
            <br />
            Please contact your administrator to request a new invitation.
          </p>
          <Button
            as={Link}
            className="w-full font-semibold text-base shadow-none"
            color="primary"
            radius="sm"
            size="lg"
            to={AppRoutesList.login}
          >
            Back to Login
          </Button>
        </div>
      </div>
    )
  }

  if (data?.isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#18181b] px-4">
        <div className="bg-[#232328] dark:bg-[#232328] rounded-xl shadow-lg p-8 max-w-md w-full text-center transition-colors duration-300">
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center justify-center rounded-full bg-red-500/10 dark:bg-red-500/10 h-14 w-14">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" fill="none" r="10" stroke="currentColor" strokeWidth="2" />
                <path
                  d="M12 8v4m0 4h.01"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
            </span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">Invitation Expired</h2>
          <p className="text-gray-400 mb-8 text-base leading-relaxed">
            Your invitation link has expired.
            <br />
            Please contact your administrator to request a new invitation.
          </p>
          <Button
            as={Link}
            className="w-full font-semibold text-base shadow-none"
            color="primary"
            radius="sm"
            size="lg"
            to="/auth/login"
          >
            Back to Login
          </Button>
        </div>
      </div>
    )
  }

  return null
}
